import { z } from "zod";
import { assertUnreachable } from "../helpers/utilities.js";
import { dataRespectsSchema } from "../helpers/zodHelpers.js";
const TeammateRoleTypes = {
  CUSTOM: "custom",
  EDITOR: "editor",
  REVIEWER: "reviewer",
  TEAM_MEMBER: "team_member"
};
const allTeammateRoleTypes = [
  TeammateRoleTypes.CUSTOM,
  TeammateRoleTypes.EDITOR,
  TeammateRoleTypes.REVIEWER,
  TeammateRoleTypes.TEAM_MEMBER
];
const teammateRoleTypeSchema = z.enum(allTeammateRoleTypes);
const predefinedTeammateRoleTypeSchema = teammateRoleTypeSchema.refine(
  (roleType) => roleType !== TeammateRoleTypes.CUSTOM
);
function isPredefinedTeammateRoleType(roleType) {
  return dataRespectsSchema(roleType, predefinedTeammateRoleTypeSchema);
}
const PermissionActions = {
  CREATE: "create",
  DELETE: "delete",
  READ: "read",
  UPDATE: "update"
};
const allPermissionActions = [
  PermissionActions.CREATE,
  PermissionActions.DELETE,
  PermissionActions.READ,
  PermissionActions.UPDATE
];
const PermissionSubjects = {
  AUTOMATIONS: "automations",
  BILLING: "billing",
  COMPANY_SETTINGS: "company_settings",
  DATA_EXTRACTION: "data_extraction",
  DEVELOPERS: "developers",
  FORMS: "forms",
  INTEGRATIONS: "integrations",
  JOB_OPENINGS: "job_openings",
  KNOWLEDGE_BASES: "knowledge_bases",
  MESSAGE_TEMPLATES: "message_templates",
  RECORDING_ASSESSMENT_TEMPLATES: "recording_assessment_templates",
  GROUPS: "groups",
  REPORTS: "reports",
  SIGNALS: "signals",
  TEAMMATES: "teammates",
  TOPICS: "topics",
  USER_CUSTOM_FIELDS: "user_custom_fields",
  WEBHOOKS: "webhooks"
};
const allPermissionSubjects = [
  PermissionSubjects.AUTOMATIONS,
  PermissionSubjects.BILLING,
  PermissionSubjects.COMPANY_SETTINGS,
  PermissionSubjects.DATA_EXTRACTION,
  PermissionSubjects.DEVELOPERS,
  PermissionSubjects.FORMS,
  PermissionSubjects.INTEGRATIONS,
  PermissionSubjects.JOB_OPENINGS,
  PermissionSubjects.KNOWLEDGE_BASES,
  PermissionSubjects.MESSAGE_TEMPLATES,
  PermissionSubjects.RECORDING_ASSESSMENT_TEMPLATES,
  PermissionSubjects.GROUPS,
  PermissionSubjects.REPORTS,
  PermissionSubjects.SIGNALS,
  PermissionSubjects.TEAMMATES,
  PermissionSubjects.TOPICS,
  PermissionSubjects.USER_CUSTOM_FIELDS,
  PermissionSubjects.WEBHOOKS
];
function isPermissionSubject(value) {
  return allPermissionSubjects.includes(value);
}
const teammatePermissionSchema = z.object({
  role: z.union([
    z.object({
      type: predefinedTeammateRoleTypeSchema
    }),
    z.object({
      type: z.literal(TeammateRoleTypes.CUSTOM),
      // id is required for custom roles (it points to the role in the "role" table -that does not exist yet-)
      id: z.string()
    })
  ])
});
function getRoleAccessForPredefinedRole(roleType) {
  switch (roleType) {
    case TeammateRoleTypes.EDITOR: {
      return allPermissionSubjects.map((subject) => ({
        action: [...allPermissionActions],
        subject
      }));
    }
    // The reviewer has access to company settings, integrations, job openings,
    // and message templates but they cannot edit (create, delete, update) them.
    case TeammateRoleTypes.REVIEWER:
      return [
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.COMPANY_SETTINGS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.INTEGRATIONS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.JOB_OPENINGS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.MESSAGE_TEMPLATES
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.RECORDING_ASSESSMENT_TEMPLATES
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.DATA_EXTRACTION
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.USER_CUSTOM_FIELDS
        },
        {
          action: [PermissionActions.CREATE],
          subject: PermissionSubjects.TEAMMATES
        },
        {
          action: [PermissionActions.CREATE],
          subject: PermissionSubjects.GROUPS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.FORMS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.KNOWLEDGE_BASES
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.SIGNALS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.REPORTS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.TOPICS
        },
        {
          action: [PermissionActions.READ],
          subject: PermissionSubjects.AUTOMATIONS
        }
      ];
    // No permissions for team members for now.
    case TeammateRoleTypes.TEAM_MEMBER: {
      return [];
    }
    default:
      assertUnreachable(roleType);
  }
}
const predefinedRoleNameByType = {
  [TeammateRoleTypes.EDITOR]: "Editor",
  [TeammateRoleTypes.REVIEWER]: "Reviewer",
  [TeammateRoleTypes.TEAM_MEMBER]: "Team Member"
};
export {
  PermissionActions,
  PermissionSubjects,
  TeammateRoleTypes,
  allPermissionActions,
  allPermissionSubjects,
  allTeammateRoleTypes,
  getRoleAccessForPredefinedRole,
  isPermissionSubject,
  isPredefinedTeammateRoleType,
  predefinedRoleNameByType,
  predefinedTeammateRoleTypeSchema,
  teammatePermissionSchema,
  teammateRoleTypeSchema
};
//# sourceMappingURL=teammatePermissions.js.map
